import { useCallback } from 'react'
import { useToast } from '../../platform/feedback/ToastProvider'
import type { Txn } from '../transactions/transactionWire'
import { localToday, type LocalDate } from './recurringRules'
import { MarkPaidError, OFF_GRID, periodAlreadyPaidCopy, useMarkPaid } from './useMarkPaid'
import { PeriodLinkError, usePeriodLinks } from './usePeriodLinks'
import type { Bill } from './recurringWire'

const LINKED = 'That transaction already belongs to another bill.'
const CASH_UNAVAILABLE = "Recording a payment without a transaction isn't available yet."

/**
 * What a choice came to, for the sheet:
 *
 * - `done` — paid; close.
 * - `closed` — it failed in a way the hook already toasted (the refetch shows
 *   the truth); close.
 * - `problem` — a 409 or 422 to show in place, with the bill that owns the
 *   transaction when there is one.
 */
export type PayOutcome = { kind: 'done' } | { kind: 'closed' } | { kind: 'problem'; message: string; owner: string | null }

/** The sentence a mark-paid 409 or 422 shows. `failed` and `already-paid` have none: the hook toasted. */
function problemCopy(e: MarkPaidError): string {
  if (e.kind === 'off-grid') return OFF_GRID
  if (e.kind === 'period-already-paid') return periodAlreadyPaidCopy(e.dueDate)
  return e.kind === 'linked' ? LINKED : CASH_UNAVAILABLE
}

/**
 * One choice in the Choose-payment sheet, filed where the server takes it
 * (#826). A transaction on a NAMED period (a skipped row) goes to
 * `POST /recurring/{id}/payments` with that date, because mark-paid refuses a
 * transaction together with a period; everything else is mark-paid, which pays
 * the period the server names (cash on a named period sends it).
 *
 * Both paths settle into the same three outcomes, so the sheet has one arm:
 * a generic failure toasts and closes on either, and a 409 on a named period
 * says which date is taken, in the same words the mark-paid 409 uses.
 */
export function usePayChoice(bill: Bill, period: LocalDate | null): { pay: (t: Txn | null) => Promise<PayOutcome>; busy: boolean } {
  const toast = useToast()
  const { markPaid, inFlight } = useMarkPaid(bill)
  const { link, busy: linking } = usePeriodLinks(bill)

  const pay = useCallback(
    async (t: Txn | null): Promise<PayOutcome> => {
      try {
        if (period !== null && t !== null) await link(t.id, period)
        else await markPaid(t === null ? { transactionId: null, paidDate: localToday(), period } : { transactionId: t.id, paidDate: t.date })
        return { kind: 'done' }
      } catch (e) {
        if (e instanceof PeriodLinkError) {
          if (e.kind === 'failed') {
            toast.show({ kind: 'error', message: e.message })
            return { kind: 'closed' }
          }
          // `filled`: the real /payments 409 is "A payment is already linked for this billing period".
          const message = e.kind === 'filled' && period !== null ? periodAlreadyPaidCopy(period) : e.message
          return { kind: 'problem', message, owner: e.recurringId }
        }
        const err = e instanceof MarkPaidError ? e : new MarkPaidError('failed', null, e)
        // Both already toasted by the hook.
        if (err.kind === 'failed' || err.kind === 'already-paid') return { kind: 'closed' }
        return { kind: 'problem', message: problemCopy(err), owner: err.recurringId }
      }
    },
    [period, link, markPaid, toast],
  )

  return { pay, busy: inFlight || linking }
}
